import logger from '../../utils/logger';
import { catchError, debounce, switchMap } from 'rxjs/operators';
import { cloneDeep } from 'lodash-es';
import { dispatch } from '../store';
import { EMPTY_ACTION } from './emptyAction';
import { Epic, ofType } from 'redux-observable';
import { getPricesForEquipmentClasses } from '../../apis/equipment';
import { isAuthInitialized } from '../../utils/authUtils';
import { LocationState, selectLocationFulfilled } from '../slice/locationSlice';
import { of, timer } from 'rxjs';
import { PriceCatalogIDComponent, PriceStatus } from '../../types/equipment';
import { setAuthIdentity, unsetAuthIdentity } from '../slice/authSlice';
import {
  PricesState,
  onLoadingPricesError,
  fetchPricesFulfilled,
  fetchPriceRequest,
  startLoadingPrices,
  updatePriceCatalog,
  resetPriceCatalog,
} from '../slice/pricesSlice';

const maxCountPerFetchPricesRequest = 100;

/**
 * Epic responsible for triggering the prices fetch API call based on specific actions.
 * Listened actions: Price fetch requests from price components and branch state updates.
 *
 * Behavior:
 * - When price loading initiates, it's removed from the queue.
 * - If an error occurs during loading, it's added to the catalog with error status.
 * - If response is missing any equipment price data, it's added to the catalog with error status.
 * - The price fetch is segmented into multiple API calls based on the defined limit.
 * - If the branch data is not present, prices will not be fetched.
 * - On app bootstrapping, if the loading queue has items, prices for them are fetched again.
 */
const fetchPricesEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(
      fetchPriceRequest.type,
      updatePriceCatalog.type,
      selectLocationFulfilled.type,
      setAuthIdentity.type,
      unsetAuthIdentity.type,
    ),
    debounce((e) => (e.type === fetchPriceRequest.type ? timer(100) : timer(0))),
    switchMap(async (e) => {
      const state = cloneDeep(state$.value);
      const location: LocationState = state.location;
      const prices: PricesState = state.prices;
      const branchId = location.branch?.branch_id;
      const companyId = state.auth.authContext?.companyId;

      if (!branchId || !isAuthInitialized(state.auth.loginStatus)) {
        return EMPTY_ACTION('At fetchPricesEpic, missing branch id');
      }

      const fetchPrices = async (list: PriceCatalogIDComponent[]) => {
        if (list.length === 0) {
          return;
        }
        dispatch(startLoadingPrices({ list }));
        try {
          const EquipmentIDs = list.map((e) => e.equipmentId);
          const prices = await getPricesForEquipmentClasses(branchId, EquipmentIDs);
          const pricesNotFound: PriceCatalogIDComponent[] = [];
          list.forEach((e1) => {
            const price = prices.find((e2) => e1.equipmentId == e2.equipment_class_id);
            if (!price) {
              pricesNotFound.push(e1);
            }
          });
          if (pricesNotFound.length > 0) {
            dispatch(
              onLoadingPricesError({
                list: pricesNotFound,
                errorStatus: PriceStatus.PRICE_NOT_FOUND,
              }),
            );
          }
          dispatch(fetchPricesFulfilled({ prices, companyId }));
          dispatch(
            updatePriceCatalog({
              equipmentIds: EquipmentIDs,
              branchId: branchId,
              companyId: companyId,
            }),
          );
        } catch (error) {
          dispatch(
            onLoadingPricesError({
              list: list,
              errorStatus: PriceStatus.ERROR,
            }),
          );
        }
      };

      let index = 0;
      let count = 0;
      const equipmentIdsMatrix: PriceCatalogIDComponent[][] = [];
      let keys: PriceCatalogIDComponent[] = [];

      if (e.type === fetchPriceRequest.type) {
        keys = Object.values(prices.fetchPriceQueue);
        if (keys.length > 0) {
          dispatch(
            updatePriceCatalog({
              equipmentIds: keys.map((e) => e.equipmentId),
              branchId: branchId,
              companyId: companyId,
            }),
          );
        }
      } else if (
        e.type === selectLocationFulfilled.type ||
        e.type === setAuthIdentity.type ||
        e.type === unsetAuthIdentity.type
      ) {
        keys = Object.values(prices.priceCatalog);
        dispatch(resetPriceCatalog());
        keys.forEach((key) => {
          dispatch(fetchPriceRequest({ equipmentId: key.equipmentId }));
        });
      } else if (e.type === updatePriceCatalog.type) {
        keys = Object.values(prices.fetchPriceQueue);

        keys.forEach((e) => {
          if (!equipmentIdsMatrix[index]) {
            equipmentIdsMatrix[index] = [];
          }
          equipmentIdsMatrix[index].push(e);
          count++;

          if (count === maxCountPerFetchPricesRequest) {
            index++;
            count = 0;
          }
        });
        equipmentIdsMatrix.forEach((e) => fetchPrices(e));
      }

      return EMPTY_ACTION('fetchPricesEpic completed');
    }),
    catchError((error) => {
      logger('error', 'at fetchPricesEpic while loading prices', error);
      return of(EMPTY_ACTION('error at fetchPricesEpic while loading prices'));
    }),
  );

export default fetchPricesEpic;
